import React, { useRef, useState } from 'react';
import { apiClient } from '../../api/client';
import { useAffiliationLabel } from '../../hooks/useAffiliationLabel';

/**
 * RSVP import for non-ticketed (free) events.
 *
 * Upload a CSV of RSVPs — one row per party (a primary guest + a party size /
 * named plus-ones). The panel parses the file in the browser, shows a preview
 * with the expanded headcount, then confirms by POSTing structured parties to
 * `/events/:id/rsvp-import`. The backend creates one order + N guest records
 * per party (no tickets, no price). Plus-ones without names become placeholder
 * guests the organizer can rename later.
 *
 * Expected columns (header row, case-insensitive; flexible aliases):
 *   name | primary | primary_name   → primary guest (required)
 *   email                           → optional
 *   phone                           → optional
 *   party_size | party | size | guests | count → total headcount incl. primary
 *   guest_names | plus_ones | names  → optional, ';' or '|' separated extra names
 *   chapter | affiliation | group    → optional
 */

interface ParsedParty {
  primaryName: string;
  email?: string;
  phone?: string;
  partySize: number;
  guestNames: string[];
  chapterAffiliation?: string;
  _warning?: string;
}

interface ImportResult {
  batchId: string;
  partiesCreated: number;
  guestsCreated: number;
  skipped: number;
  warnings: string[];
  totalGuests: number;
  referenceIds?: string[];
}

const HEADER_ALIASES: Record<string, keyof ParsedParty | 'ignore'> = {
  name: 'primaryName',
  primary: 'primaryName',
  'primary name': 'primaryName',
  primaryname: 'primaryName',
  guest: 'primaryName',
  email: 'email',
  'e-mail': 'email',
  phone: 'phone',
  mobile: 'phone',
  'party size': 'partySize',
  party_size: 'partySize',
  party: 'partySize',
  size: 'partySize',
  guests: 'partySize',
  count: 'partySize',
  headcount: 'partySize',
  'guest names': 'guestNames',
  guest_names: 'guestNames',
  plus_ones: 'guestNames',
  'plus ones': 'guestNames',
  names: 'guestNames',
  chapter: 'chapterAffiliation',
  affiliation: 'chapterAffiliation',
  group: 'chapterAffiliation',
};

/** Minimal CSV line splitter that respects double-quoted fields. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ',') { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseCsv(text: string): { parties: ParsedParty[]; error?: string } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return { parties: [], error: 'File needs a header row and at least one RSVP row.' };

  const headers = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
  const colMap = headers.map((h) => HEADER_ALIASES[h] ?? 'ignore');
  if (!colMap.includes('primaryName')) {
    return { parties: [], error: 'Could not find a name column (expected one of: name, primary, guest).' };
  }

  const parties: ParsedParty[] = [];
  for (let r = 1; r < lines.length; r++) {
    const cells = splitCsvLine(lines[r]);
    const row: Partial<Record<keyof ParsedParty, string>> = {};
    colMap.forEach((key, idx) => {
      if (key !== 'ignore') (row as any)[key] = cells[idx] ?? '';
    });

    const primaryName = (row.primaryName || '').trim();
    if (!primaryName) continue; // silently skip blank-name rows

    const guestNames = (row.guestNames || '')
      .split(/[;|]/)
      .map((s) => s.trim())
      .filter(Boolean);

    const rawSize = parseInt((row.partySize as string) || '', 10);
    const namedExtras = guestNames.filter((n) => n.toLowerCase() !== primaryName.toLowerCase()).length;
    const impliedMin = 1 + namedExtras;
    let partySize = Number.isNaN(rawSize) ? impliedMin : Math.max(rawSize, impliedMin);
    let _warning: string | undefined;
    if (partySize < 1) partySize = 1;
    if (partySize > 50) { partySize = 50; _warning = 'Party size capped at 50.'; }

    parties.push({
      primaryName,
      email: (row.email || '').trim() || undefined,
      phone: (row.phone || '').trim() || undefined,
      partySize,
      guestNames,
      chapterAffiliation: (row.chapterAffiliation || '').trim() || undefined,
      _warning,
    });
  }

  if (parties.length === 0) return { parties: [], error: 'No valid RSVP rows found.' };
  return { parties };
}

interface ResetPreview {
  guestsToDelete: number;
  ordersToDelete: number;
  blockingGuestNames: string[];
  canReset: boolean;
}

export function RsvpImportPanel({
  eventId,
  onImported,
  onReset,
}: {
  eventId: string;
  onImported?: () => void;
  onReset?: () => void;
}) {
  const { label: affLabel, lower: affLower } = useAffiliationLabel();
  const fileRef = useRef<HTMLInputElement>(null);
  const [parties, setParties] = useState<ParsedParty[]>([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  // "Clean slate" reset — removes ALL RSVP guests/parties for the event
  // (including data imported before upload-history tracking existed).
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPreview, setResetPreview] = useState<ResetPreview | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');

  const totalGuests = parties.reduce((s, p) => s + p.partySize, 0);

  function handleFile(file: File) {
    setError('');
    setResult(null);
    if (!/\.csv$/i.test(file.name)) {
      setError('Please upload a .csv file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const { parties: parsed, error: parseErr } = parseCsv(String(reader.result || ''));
      if (parseErr) { setError(parseErr); setParties([]); setFileName(''); return; }
      setParties(parsed);
      setFileName(file.name);
    };
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsText(file);
  }

  async function confirm() {
    if (parties.length === 0) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await apiClient<ImportResult>(`/events/${eventId}/rsvp-import`, {
        method: 'POST',
        body: {
          fileName,
          parties: parties.map((p) => ({
            primaryName: p.primaryName,
            email: p.email,
            phone: p.phone,
            partySize: p.partySize,
            guestNames: p.guestNames,
            chapterAffiliation: p.chapterAffiliation,
          })),
        },
      });
      setResult(res);
      setParties([]);
      setFileName('');
      if (fileRef.current) fileRef.current.value = '';
      onImported?.();
    } catch (e: any) {
      setError(e?.message || 'Import failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function openReset() {
    setResetOpen(true);
    setResetPreview(null);
    setResetError('');
    setResetLoading(true);
    try {
      const p = await apiClient<ResetPreview>(`/events/${eventId}/rsvp-reset-preview`);
      setResetPreview(p);
    } catch (e: any) {
      setResetError(e?.message || 'Could not load reset preview.');
    } finally {
      setResetLoading(false);
    }
  }

  function closeReset() {
    setResetOpen(false);
    setResetPreview(null);
    setResetError('');
  }

  async function doReset() {
    setResetting(true);
    setResetError('');
    try {
      await apiClient(`/events/${eventId}/rsvp-reset`, { method: 'DELETE' });
      closeReset();
      onReset?.();
    } catch (e: any) {
      setResetError(e?.message || 'Reset failed. You may not have permission for this action.');
    } finally {
      setResetting(false);
    }
  }

  function reset() {
    setParties([]);
    setFileName('');
    setError('');
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  /** Build and download a starter CSV so the organizer knows the exact format. */
  function downloadTemplate() {
    const rows = [
      ['name', 'party_size', 'guest_names', 'email', 'phone', affLower],
      ['Jane Smith', '3', 'John Smith; Baby Smith', 'jane@example.com', '555-0100', ''],
      ['Michael Lee', '2', 'Dana Lee', '', '', ''],
      ['Priya Patel', '1', '', 'priya@example.com', '', ''],
    ];
    const csv = rows
      .map((r) => r.map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'guest-list-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={styles.panel}>
      <h3 style={styles.title}>🎉 Import RSVPs</h3>
      <p style={styles.subtitle}>
        Upload a guest list for this non-ticketed event. One row per party — a primary guest plus a
        party size or named plus-ones. We’ll create a guest record for everyone (no tickets, no charge).
      </p>

      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {result ? (
        <div style={styles.success}>
          <strong>Imported {result.partiesCreated} part{result.partiesCreated === 1 ? 'y' : 'ies'}</strong> —{' '}
          {result.guestsCreated} guest{result.guestsCreated === 1 ? '' : 's'} added.
          {result.skipped > 0 && <span> {result.skipped} row(s) skipped.</span>}
          {' '}Event now has {result.totalGuests} guest{result.totalGuests === 1 ? '' : 's'}.
          {result.warnings.length > 0 && (
            <ul style={styles.warnList}>
              {result.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
          <button style={styles.secondaryBtn} onClick={reset}>Import another file</button>
        </div>
      ) : parties.length === 0 ? (
        <>
          <div
            style={styles.drop}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
          >
            <div style={styles.dropIcon}>📄</div>
            <div style={styles.dropText}>Click or drop a <strong>.csv</strong> file here</div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>
          <div style={styles.templateRow}>
            <button type="button" style={styles.templateBtn} onClick={downloadTemplate}>
              ⬇ Download CSV template
            </button>
            <span style={styles.templateHint}>Not sure of the format? Start from our template.</span>
          </div>
          <details style={styles.help}>
            <summary style={styles.helpSummary}>Expected file format</summary>
            <p style={styles.helpBody}>
              A header row then one row per RSVP. Recognized columns (any order):
            </p>
            <ul style={styles.helpBody}>
              <li><code>name</code> — primary guest (required)</li>
              <li><code>party_size</code> — total headcount including the primary (e.g. 3 for a “+2”)</li>
              <li><code>guest_names</code> — optional; names of the plus-ones, separated by <code>;</code> or <code>|</code></li>
              <li><code>email</code>, <code>phone</code>, <code>{affLower}</code> — all optional</li>
            </ul>
            <p style={styles.helpBody}>
              If a party size is larger than the names given, the extra seats become placeholder guests
              (“Guest of Jane #1”) you can rename later.
            </p>
          </details>
        </>
      ) : (
        <>
          <div style={styles.previewHead}>
            <div>
              <strong>{fileName}</strong> · {parties.length} part{parties.length === 1 ? 'y' : 'ies'} ·{' '}
              <strong>{totalGuests}</strong> total guest{totalGuests === 1 ? '' : 's'}
            </div>
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Primary</th>
                  <th style={styles.thNum}>Party size</th>
                  <th style={styles.th}>Plus-ones</th>
                  <th style={styles.th}>{affLabel}</th>
                </tr>
              </thead>
              <tbody>
                {parties.map((p, i) => {
                  const extras = p.guestNames.filter((n) => n.toLowerCase() !== p.primaryName.toLowerCase());
                  const placeholders = Math.max(0, p.partySize - 1 - extras.length);
                  return (
                    <tr key={i}>
                      <td style={styles.td}>{p.primaryName}{p.email ? <span style={styles.dim}> · {p.email}</span> : null}</td>
                      <td style={styles.tdNum}>{p.partySize}</td>
                      <td style={styles.td}>
                        {extras.length === 0 && placeholders === 0 ? <span style={styles.dim}>—</span> : (
                          <>
                            {extras.join(', ')}
                            {placeholders > 0 && (
                              <span style={styles.dim}>{extras.length ? ', ' : ''}+{placeholders} unnamed</span>
                            )}
                          </>
                        )}
                      </td>
                      <td style={styles.td}>{p.chapterAffiliation || <span style={styles.dim}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={styles.actions}>
            <button style={styles.secondaryBtn} onClick={reset} disabled={submitting}>Cancel</button>
            <button style={styles.primaryBtn} onClick={confirm} disabled={submitting}>
              {submitting ? 'Importing…' : `Import ${parties.length} party / ${totalGuests} guests`}
            </button>
          </div>
        </>
      )}

      {/* Clean slate — remove all RSVP guests/parties so you can re-upload. */}
      <div style={styles.dangerZone}>
        <div style={styles.dangerText}>
          <strong>Start over?</strong> Remove every RSVP guest and party for this event (including
          any list uploaded earlier) so you can re-upload a clean file.
        </div>
        <button type="button" style={styles.resetLink} onClick={openReset}>
          Clear all RSVP guests
        </button>
      </div>

      {resetOpen && (
        <div style={styles.modalOverlay} onClick={closeReset}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Clear all RSVP guests?</h3>
            <p style={styles.modalBody}>
              This permanently deletes <strong>every</strong> RSVP guest and party for this event.
              Use it to start fresh before re-uploading a corrected list. This cannot be undone.
            </p>

            {resetError && <div style={styles.error}>{resetError}</div>}

            {resetLoading ? (
              <div style={styles.modalBody}>Checking what would be removed…</div>
            ) : resetPreview ? (
              <>
                <div style={styles.previewBox}>
                  <strong>{resetPreview.guestsToDelete}</strong> guest
                  {resetPreview.guestsToDelete === 1 ? '' : 's'} and{' '}
                  <strong>{resetPreview.ordersToDelete}</strong> part
                  {resetPreview.ordersToDelete === 1 ? 'y' : 'ies'} will be deleted.
                </div>
                {resetPreview.blockingGuestNames.length > 0 && (
                  <div style={styles.blockBox}>
                    <strong>Cannot clear yet.</strong> {resetPreview.blockingGuestNames.length} guest
                    {resetPreview.blockingGuestNames.length === 1 ? ' is' : 's are'} already seated or
                    checked in:
                    <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>
                      {resetPreview.blockingGuestNames.slice(0, 10).join(', ')}
                      {resetPreview.blockingGuestNames.length > 10 &&
                        ` +${resetPreview.blockingGuestNames.length - 10} more`}
                    </div>
                    <div style={{ marginTop: '0.4rem', fontStyle: 'italic' }}>
                      Unassign their seats / undo check-in first, then try again.
                    </div>
                  </div>
                )}
              </>
            ) : null}

            <div style={styles.actions}>
              <button style={styles.secondaryBtn} onClick={closeReset} disabled={resetting}>
                Cancel
              </button>
              <button
                style={{
                  ...styles.dangerBtn,
                  ...(!resetPreview?.canReset || resetting ? styles.dangerBtnDisabled : {}),
                }}
                onClick={doReset}
                disabled={!resetPreview?.canReset || resetting}
              >
                {resetting ? 'Clearing…' : 'Clear all RSVP guests'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '1.25rem' },
  title: { color: '#0f2d1f', fontSize: '1.1rem', margin: '0 0 0.25rem 0' },
  subtitle: { color: '#666', fontSize: '0.85rem', margin: '0 0 1rem 0', lineHeight: 1.4 },
  drop: { border: '2px dashed #c9a84c', borderRadius: '10px', padding: '2rem', textAlign: 'center', cursor: 'pointer', background: '#fffdf5' },
  dropIcon: { fontSize: '1.75rem' },
  dropText: { color: '#555', fontSize: '0.9rem', marginTop: '0.5rem' },
  templateRow: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.75rem', flexWrap: 'wrap' as const },
  templateBtn: { background: '#fff', color: '#1a6b3c', border: '1px solid #1a6b3c', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  templateHint: { fontSize: '0.78rem', color: '#888' },
  help: { marginTop: '1rem' },
  helpSummary: { cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#1a6b3c' },
  helpBody: { fontSize: '0.82rem', color: '#555', lineHeight: 1.5 },
  previewHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.9rem', color: '#333' },
  tableWrap: { maxHeight: '340px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '0.5rem 0.7rem', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', color: '#555', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', position: 'sticky', top: 0 },
  thNum: { textAlign: 'right', padding: '0.5rem 0.7rem', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', color: '#555', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', position: 'sticky', top: 0 },
  td: { padding: '0.5rem 0.7rem', borderBottom: '1px solid #f0f0f0', color: '#333' },
  tdNum: { padding: '0.5rem 0.7rem', borderBottom: '1px solid #f0f0f0', color: '#333', textAlign: 'right', fontWeight: 600 },
  dim: { color: '#999' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1rem' },
  primaryBtn: { background: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  secondaryBtn: { background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '6px', padding: '0.6rem 1.1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
  error: { color: '#d32f2f', background: '#fdecea', padding: '0.6rem 0.9rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem', cursor: 'pointer' },
  success: { color: '#0f2d1f', background: '#ecfdf5', border: '1px solid #6ee7b7', padding: '0.9rem 1rem', borderRadius: '8px', fontSize: '0.88rem', lineHeight: 1.5 },
  warnList: { margin: '0.5rem 0 0.75rem 1rem', fontSize: '0.8rem', color: '#92400e' },
  dangerZone: { marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px dashed #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' as const },
  dangerText: { fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.4, flex: 1, minWidth: '200px' },
  resetLink: { background: 'none', border: '1px solid #e5a5a5', color: '#991b1b', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '480px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' },
  modalTitle: { margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: '#0f2d1f' },
  modalBody: { fontSize: '0.88rem', color: '#555', lineHeight: 1.5, margin: '0 0 1rem 0' },
  previewBox: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#991b1b', marginBottom: '0.75rem' },
  blockBox: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#92400e', marginBottom: '0.75rem' },
  dangerBtn: { background: '#d32f2f', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  dangerBtnDisabled: { background: '#e5a5a5', cursor: 'not-allowed' },
};
